import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import { alpha, styled } from '@mui/material/styles';

export type InlineEditFieldSize = 'small' | 'medium' | 'large';

/** Input height per size. `medium` (default) is 36px per design. */
export const SIZE_INPUT_HEIGHT: Record<InlineEditFieldSize, number> = {
  small: 30,
  medium: 36,
  large: 44,
};

/** Narrowest the editing field gets, so the ✕/✓ card always fits under it. */
export const MIN_FIELD_WIDTH = 200;

/** Font size of the displayed value / editing input per size. */
export const SIZE_FONT_SIZE: Record<InlineEditFieldSize, number> = {
  small: 12,
  medium: 14,
  large: 16,
};

/** Class on the pencil button so the value area can reveal it on hover. */
export const INLINE_EDIT_PENCIL_CLASS = 'InlineEditField-pencil';

export const Root = styled('span')(() => ({
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  minWidth: 0,
  maxWidth: '100%',
  // Reserve room for the pencil so the layout doesn't shift on hover: Figma draws a
  // 16px icon 4px after the name.
  paddingRight: 20,
  // Reveal the pencil while hovering anywhere over the value. Keyboard focus reveals it
  // through its own `:focus-visible`, so the focus it gets back after a mouse save stays
  // hidden.
  [`&:hover .${INLINE_EDIT_PENCIL_CLASS}`]: {
    opacity: 1,
  },
}));

/** The text shown when not editing: `value`, or a just-committed edit awaiting it. */
export const Display = styled('span', {
  shouldForwardProp: (prop) => prop !== '$size' && prop !== '$editable' && prop !== '$hidden',
})<{ $size: InlineEditFieldSize; $editable: boolean; $hidden?: boolean }>(
  ({ theme, $size, $editable, $hidden }) => ({
    display: 'block',
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    fontSize: SIZE_FONT_SIZE[$size],
    fontWeight: 700,
    color: theme.palette.text.primary,
    cursor: $editable ? 'inherit' : 'default',
    // Kept in the layout (so the portal field can measure the value box) but
    // hidden while the editing input is shown on top of it.
    visibility: $hidden ? 'hidden' : 'visible',
  }),
);

export const EditIconButton = styled(IconButton)(() => ({
  position: 'absolute',
  top: '50%',
  // The 24px hit target centred on the drawn 16px icon box.
  right: -4,
  transform: 'translateY(-50%)',
  opacity: 0,
  transition: 'opacity 150ms ease, color 150ms ease',
  // Revealed when the surrounding value area is hovered/focused.
  '&:hover, &:focus-visible': {
    opacity: 1,
  },
}));

export const EditLayer = styled('span')(() => ({
  display: 'block',
  flexGrow: 1,
  width: '100%',
  minWidth: `min(${MIN_FIELD_WIDTH}px, 100%)`,
}));

export const PortalContainer = styled('div')(() => ({
  position: 'absolute',
  zIndex: 20000,
}));

export const Form = styled('form')(() => ({
  position: 'relative',
  width: '100%',
}));

export const StyledInput = styled(InputBase, {
  shouldForwardProp: (prop) => prop !== '$size',
})<{ $size: InlineEditFieldSize }>(({ theme, $size }) => ({
  // The field is as wide as its text: a hidden copy of the value (`data-value`, drawn by
  // `::after`) shares the input's grid cell and sizes it, so the whole value shows and the
  // field grows while typing. A capped field shrinks the cell; the input then scrolls.
  display: 'inline-grid',
  gridTemplateColumns: 'minmax(0, 1fr)',
  alignItems: 'center',
  height: SIZE_INPUT_HEIGHT[$size],
  width: '100%',
  padding: '0 16px',
  // Figma `inline-edit-input` (Active): white, 4px corners, no stroke, `overlay-shadows/floating-card`.
  backgroundColor: theme.palette.background.paper,
  borderRadius: 4,
  boxShadow: theme.shadows[1],
  '& .MuiInputBase-input, &::after': {
    gridArea: '1 / 1',
    padding: 0,
    fontSize: SIZE_FONT_SIZE[$size],
    fontWeight: 500,
  },
  '& .MuiInputBase-input': {
    color: theme.palette.text.primary,
  },
  '&::after': {
    // The trailing space keeps room for the caret after the last character.
    content: 'attr(data-value) " "',
    height: 0,
    overflow: 'hidden',
    visibility: 'hidden',
    whiteSpace: 'pre',
  },
}));

/** Where the field puts the ✕/✓ card: under its right edge, as Figma draws it. */
export const ActionsAnchor = styled('div')(() => ({
  position: 'absolute',
  right: 0,
  top: 'calc(100% + 3px)',
  zIndex: 1,
}));

export const Backdrop = styled('div')(() => ({
  position: 'fixed',
  inset: 0,
  zIndex: 19999,
}));

/** Figma `inline-edit-input` (Active) action card: white, 4px corners, 8px padding and gap. */
export const ActionsCard = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1),
  borderRadius: 4,
  backgroundColor: theme.palette.background.paper,
  // `overlay-shadows/floating-card`, matching the field.
  boxShadow: theme.shadows[1],
}));

/** ✕: Figma `icon/24/close` in `neutral-opacity/60`, which hover keeps. */
export const CancelButton = styled(IconButton)(({ theme }) => {
  const ink = alpha(theme.palette.grey[0], 0.6);
  return { color: ink, '&:hover': { color: ink } };
});

/** ✓: Figma `aivo-icon / selected` in the brand teal, which hover keeps. */
export const ConfirmButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.primary.main,
  '&:hover': { color: theme.palette.primary.main },
}));
